/**
 * PlanLimitBanner を TanStack Query + fetchSiteUsage で自動接続するラッパ.
 *
 * usage API を siteId 単位でキャッシュし、対応 resource の current/limit を PlanLimitBanner に流し込む。
 * 80% 到達時は PostHog `plan_limit_warning_shown` を 1 度だけ発火する。
 *
 * props.resource に 'media' を渡した場合、usage API 側の 'media_bytes' キーにマップする。
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { fetchSiteUsage, type SiteUsage, type SiteUsageMetric } from '../lib/api-client.js';
import { trackEvent } from '../lib/posthog.js';
import { PlanLimitBanner, type PlanResourceLabel } from './PlanLimitBanner.js';

const USAGE_STALE_MS = 30_000; // 30s — Banner 目的で頻回呼びしすぎないレベル

function pickMetric(usage: SiteUsage, resource: PlanResourceLabel): SiteUsageMetric {
  switch (resource) {
    case 'sites':
      return usage.limits.sites;
    case 'posts':
      return usage.limits.posts;
    case 'media':
      return usage.limits.media_bytes;
    case 'members':
      return usage.limits.members;
    case 'webhooks':
      return usage.limits.webhooks;
    case 'custom_domains':
      return usage.limits.custom_domains;
  }
}

export function usePlanUsage(siteId: string | undefined) {
  return useQuery({
    queryKey: ['sites', siteId, 'usage'],
    queryFn: () => fetchSiteUsage(siteId as string),
    enabled: !!siteId,
    staleTime: USAGE_STALE_MS,
    retry: 1,
  });
}

interface PlanLimitBannerConnectedProps {
  siteId: string | undefined;
  resource: PlanResourceLabel;
  /** バナーの追加 hint テキスト (省略可) */
  hint?: React.ReactNode;
  /** アップグレード導線。未指定なら /sites/:siteId/settings?tab=billing へ */
  onUpgradeClick?: () => void;
}

export function PlanLimitBannerConnected({
  siteId,
  resource,
  hint,
  onUpgradeClick,
}: PlanLimitBannerConnectedProps) {
  const navigate = useNavigate();
  const { data: usage } = usePlanUsage(siteId);
  const warnedRef = useRef(false);

  const metric = usage ? pickMetric(usage, resource) : null;

  // 80% 到達時に PostHog イベントを発火 (同一 mount 内では 1 回だけ)
  useEffect(() => {
    if (!usage || !metric) return;
    if (metric.limit === 'unlimited' || typeof metric.limit !== 'number' || metric.limit <= 0)
      return;
    const ratio = metric.current / metric.limit;
    if (ratio >= 0.8 && !warnedRef.current) {
      warnedRef.current = true;
      try {
        trackEvent('plan_limit_warning_shown', {
          resource,
          planId: usage.planId,
          current: metric.current,
          limit: metric.limit,
        });
      } catch {
        // ignore
      }
    }
  }, [metric, resource, usage]);

  if (!usage || !metric) return null;

  const handleUpgrade =
    onUpgradeClick ??
    (siteId
      ? () => {
          navigate({ to: '/sites/$siteId/settings/billing', params: { siteId } });
        }
      : undefined);

  return (
    <PlanLimitBanner
      resource={resource}
      current={metric.current}
      limit={metric.limit}
      planId={usage.planId}
      {...(handleUpgrade ? { onUpgradeClick: handleUpgrade } : {})}
      {...(hint !== undefined ? { hint } : {})}
    />
  );
}
